// PRICING_ENGINE_PLAN H.4 / PR #14 — price-impact dialog.
//
// Shown after an admin updates a price-bearing field on a catalog entity
// (Material/Edge/Hardware/Lighting/DrawerSystemVariant). Queries the backend
// for the list of non-terminal projects whose AUTO line items would be
// affected, then offers a one-click recompute that re-runs the pricing engine
// for each impacted project (each project is recomputed in the same
// transactional, audit-logged path used by manual project recomputes).

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PriceImpactReport, RecomputeImpactResponse } from '@kreart/shared';
import { invalidateAllProjectTenders } from '@/features/project-organization/hooks';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { describeError, describeServerSentence } from '@/lib/form/errorMessages';

export interface PriceImpactDialogProps {
  /** Set null/undefined to close the dialog. When set, dialog opens and queries impact. */
  target: { kind: string; id: string; label: string } | null;
  onClose: () => void;
  /** Function that fetches `{ totalProjects, totalItems, projects[] }` for the entity. */
  fetchImpact: (id: string) => Promise<PriceImpactReport>;
  /** Function that triggers the backend recompute. Returns a summary. */
  recompute: (id: string) => Promise<RecomputeImpactResponse>;
}

export function PriceImpactDialog({
  target,
  onClose,
  fetchImpact,
  recompute,
}: PriceImpactDialogProps) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [result, setResult] = useState<RecomputeImpactResponse | null>(null);

  // Reset summary state every time we re-open.
  useEffect(() => {
    if (target) setResult(null);
  }, [target?.id, target?.kind]);

  const impact = useQuery({
    queryKey: ['price-impact', target?.kind, target?.id],
    queryFn: () => fetchImpact(target!.id),
    enabled: !!target,
    staleTime: 0,
  });

  const recomputeMut = useMutation({
    mutationFn: () => recompute(target!.id),
    onSuccess: (r) => {
      setResult(r);
      // Invalidate project lists so totals refresh anywhere they're displayed.
      void qc.invalidateQueries({ queryKey: ['projects'] });
      // A catalog price change moves every project's BOM grand total with no
      // project-side write at all, so neither of those roots prefix-matches
      // ['project-bom'] or ['project-organization'] — invalidate them directly.
      // Active-only (the default): a repricing with many cached projects would
      // otherwise fire a refetch storm, and only the mounted view matters.
      void qc.invalidateQueries({ queryKey: ['project-bom'] });
      // The line items themselves were re-priced too, and each type caches under
      // its own root — none of which the roots above prefix-match. Leaving them
      // is what makes a project tab open during a repricing show a refreshed
      // header over pre-recompute row prices: the exact header-disagrees-with-
      // the-rooms confusion this dialog just went to the trouble of fixing in
      // the database.
      for (const root of [
        'project-cabinets',
        'project-cabinet-hardware',
        'project-hardware',
        'project-panels',
        'project-countertops',
        'project-drawers',
        'project-elements',
        'project-lighting',
      ]) {
        void qc.invalidateQueries({ queryKey: [root] });
      }
      void invalidateAllProjectTenders(qc);
    },
  });

  const handleClose = () => {
    if (recomputeMut.isPending) return;
    onClose();
  };

  return (
    <Dialog open={!!target} onOpenChange={(open) => (!open ? handleClose() : undefined)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('priceImpact.title', {
              defaultValue: 'Price changed — recompute affected projects?',
            })}
          </DialogTitle>
          <DialogDescription>{target?.label}</DialogDescription>
        </DialogHeader>

        {impact.isLoading && (
          <p className="text-sm text-muted-foreground">
            {t('common.loading', { defaultValue: 'Loading…' })}
          </p>
        )}
        {impact.isError && (
          <p className="text-sm text-destructive">
            {t('priceImpact.loadError', {
              defaultValue: 'Could not load impact report.',
            })}{' '}
            {describeError(t, impact.error)}
          </p>
        )}

        {impact.data && !result && (
          <div className="space-y-3">
            {impact.data.totalProjects === 0 ? (
              <p className="text-sm">
                {t('priceImpact.none', {
                  defaultValue:
                    'No non-terminal projects use this catalog entry — no recompute needed.',
                })}
              </p>
            ) : (
              <>
                <p className="text-sm">
                  {t('priceImpact.summary', {
                    defaultValue:
                      '{{projects}} project(s) with {{items}} AUTO line item(s) reference this entry.',
                    projects: impact.data.totalProjects,
                    items: impact.data.totalItems,
                  })}
                </p>
                <ul className="max-h-48 overflow-y-auto rounded border bg-muted/30 p-2 text-xs">
                  {impact.data.projects.map((p) => (
                    <li key={p.id} className="flex justify-between gap-2 py-0.5">
                      <span className="truncate">
                        <span className="font-mono text-muted-foreground">{p.code}</span>{' '}
                        <span>{p.title}</span>
                        <span className="ml-2 text-muted-foreground">[{p.status}]</span>
                      </span>
                      <span className="shrink-0 font-mono">{p.itemCount}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        )}

        {result && (
          <div className="space-y-1 text-sm">
            <p>
              {t('priceImpact.recomputedSummary', {
                defaultValue: 'Recomputed {{recomputed}} item(s) across {{projects}} project(s).',
                recomputed: result.totalRecomputed,
                projects: result.projectsAffected,
              })}
            </p>
            {result.errors.length > 0 && (
              <details className="text-xs text-destructive">
                <summary>
                  {t('priceImpact.errorsCount', {
                    defaultValue: '{{count}} error(s)',
                    count: result.errors.length,
                  })}
                </summary>
                <ul className="mt-1 space-y-0.5">
                  {result.errors.slice(0, 20).map((e, i) => (
                    <li key={i} className="font-mono">
                      [{e.itemType}] {e.itemId}: {describeServerSentence(t, e.message)}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}

        <DialogFooter>
          {result ? (
            <Button onClick={handleClose}>{t('common.close', { defaultValue: 'Close' })}</Button>
          ) : (
            <>
              <Button variant="outline" onClick={handleClose} disabled={recomputeMut.isPending}>
                {t('priceImpact.later', { defaultValue: 'Later' })}
              </Button>
              <Button
                onClick={() => recomputeMut.mutate()}
                disabled={recomputeMut.isPending || !impact.data || impact.data.totalProjects === 0}
              >
                {recomputeMut.isPending
                  ? t('priceImpact.recomputing', { defaultValue: 'Recomputing…' })
                  : t('priceImpact.recomputeNow', { defaultValue: 'Recompute now' })}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
